import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { AccordionComponent, AccordionPanelComponent } from 'ngx-bootstrap/accordion';
import { JsonTimesheetModel } from '../../../models/low-level/timesheet.model';
import { JsonExpenseModel } from '../../../models/low-level/expense.model';
import { JsonTimesheetNotificationModel } from '../../../models/low-level/timesheet-notification.model';
import { JsonTimesheetManualEntryModel } from '../../../models/complex/timesheet-manual-entry.model';
import {
    JsonTimesheetAddressModel,
    JsonTimesheetAddressModelWithObject,
} from '../../../models/complex/timesheet-address.model';
import { formatAddressModel, workCodeLabel } from '../pace-spector.utils';
import { NotificationSpectorComponent } from '../notification-spector/notification-spector.component';
import { TranslateModule } from '@ngx-translate/core';
import { isDefined } from '../../../utilities/is-defined';

@Component({
    selector: 'pace-core-timesheet-spector',
    imports: [
        CommonModule,
        AlertComponent,
        AccordionComponent,
        AccordionPanelComponent,
        NotificationSpectorComponent,
        TranslateModule,
    ],
    templateUrl: './timesheet-spector.component.html',
    styleUrl: './timesheet-spector.component.css',
})
export class TimesheetSpectorComponent {
    public model = input<JsonTimesheetModel | null | undefined>(undefined);

    public hasData = computed(() => !!this.model());
    public notifications = computed<JsonTimesheetNotificationModel[]>(() => this.model()?.notifications ?? []);
    public manualEntries = computed<JsonTimesheetManualEntryModel[]>(() => this.model()?.manualTimesheetEntries ?? []);
    public additionalExpenses = computed<JsonExpenseModel[]>(() => this.model()?.additionalExpenses ?? []);

    public modelDisplay = computed(() => {
        const model = this.model();

        return {
            startOfWork: this.fmtDateTime(model?.startOfWork),
            isSubmitted: model?.isSubmitted ?? false,
            attendanceType: model?.attendanceType ?? '--',
            startAddressValue: model?.startAddressValue ?? '--',
            endAddressValue: model?.endAddressValue ?? '--',
            saveTimestamp: this.fmtEpoch(model?.saveTimestamp),
        };
    });

    public summaryCountText = computed(() => {
        const n = this.notifications().length;
        const m = this.manualEntries().length;
        const e = this.additionalExpenses().length;
        return `${n} notif // ${m} manual // ${e} exp`;
    });

    public resetLocal = output<void>();
    public downloadJson = output<JsonTimesheetModel>();

    public copyJson(): void {
        const raw = this.model();
        if (!raw) {
            return;
        }
        const text = JSON.stringify(raw, null, 2);
        navigator.clipboard?.writeText(text).catch(console.error);
    }

    public triggerDownloadJson(): void {
        const model = this.model();
        if (!model) {
            return;
        }
        this.downloadJson.emit(model);
    }

    protected fmtDateTime(v?: string | null): string {
        if (!v) {
            return '--';
        }
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.toLocaleString();
    }

    private fmtEpoch(v?: number | null): string {
        if (v === null || v === undefined) {
            return '--';
        }
        const d = new Date(v);
        return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
    }

    protected formatAddress(a?: JsonTimesheetAddressModel | JsonTimesheetAddressModelWithObject | null): string {
        // Migration logic to support Timesheet address model
        let addressModel: JsonTimesheetAddressModel = {
            street: '',
            city: '',
            postCode: '',
            country: '',
            addrText: '',
            addrNumber: '',
            statesOfUSA: '',
        };
        if (isDefined(a)) {
            addressModel = {
                street: isDefined(a.street) && typeof a.street === 'object' ? a.street.value : a.street,
                city: isDefined(a.city) && typeof a.city === 'object' ? a.city.value : a.city,
                postCode: isDefined(a.postCode) && typeof a.postCode === 'object' ? a.postCode.value : a.postCode,
                country: isDefined(a.country) && typeof a.country === 'object' ? a.country.value : a.country,
                addrText: isDefined(a.addrText) && typeof a.addrText === 'object' ? a.addrText.value : a.addrText,
                addrNumber:
                    isDefined(a.addrNumber) && typeof a.addrNumber === 'object' ? a.addrNumber.value : a.addrNumber,
                statesOfUSA: '',
            };
        }
        return formatAddressModel(addressModel);
    }

    protected readonly workCodeLabel = workCodeLabel;

    public triggerResetLocal(): void {
        this.resetLocal.emit();
    }
}
